import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthState } from "@/hooks/useAuthState";

const Terms = () => {
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <main className="flex-1 container mx-auto px-4 py-12">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-3xl md:text-4xl font-bold text-center">
              Termos de Uso
            </CardTitle>
            <p className="text-center text-muted-foreground mt-2">
              Última atualização: 14 de outubro de 2025
            </p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-3">1. Aceitação dos Termos</h2>
              <p className="text-muted-foreground">
                Ao acessar e usar a plataforma Live Stay, você concorda com estes Termos de Uso e
                com todas as leis e regulamentos aplicáveis. Se você não concordar com algum destes
                termos, está proibido de usar ou acessar este site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2. Sobre o Serviço</h2>
              <p className="text-muted-foreground">
                A Live Stay é uma plataforma de reservas de temporada que conecta proprietários de imóveis 
                a hóspedes interessados em estadias no Porto 2 Life Resort, localizado em Muro Alto, 
                Porto de Galinhas - PE. Oferecemos uma experiência completa de reserva online, com 
                infraestrutura premium e atendimento personalizado.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">2.1. Horário do Site</h2>
              <p className="text-muted-foreground">
                Todos os horários exibidos na plataforma Live Stay são baseados no horário oficial de Brasília, 
                Brasil (UTC-3). Isso inclui horários de mensagens, reservas, check-in, check-out e qualquer 
                outra informação temporal apresentada no sistema. O horário de Brasília é o padrão nacional 
                brasileiro e será utilizado para todas as operações e comunicações da plataforma.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">3. Cadastro e Conta</h2>
              <p className="text-muted-foreground mb-2">
                Para utilizar nossa plataforma, você deve:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Ter no mínimo 18 anos de idade</li>
                <li>Fornecer informações verdadeiras, precisas e completas</li>
                <li>Manter suas informações atualizadas</li>
                <li>Manter a confidencialidade de sua senha</li>
                <li>Ser responsável por todas as atividades realizadas em sua conta</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">4. Anúncios de Propriedades</h2>
              <p className="text-muted-foreground mb-2">
                Proprietários que anunciam imóveis no Porto 2 Life devem:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Fornecer descrições precisas e fotos reais da propriedade</li>
                <li>Ter autorização legal para alugar a propriedade</li>
                <li>Cumprir as regras do condomínio Porto 2 Life</li>
                <li>Manter disponibilidade atualizada</li>
                <li>Responder prontamente às solicitações de reserva</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">5. Reservas e Pagamentos</h2>
              <p className="text-muted-foreground">
                Todas as reservas estão sujeitas à disponibilidade e confirmação do proprietário. 
                Os pagamentos são processados de forma segura através de nossa plataforma. Taxas de 
                serviço podem ser aplicadas. A Live Stay se reserva o direito de recusar qualquer reserva.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">6. Política de Cancelamento</h2>
              <p className="text-muted-foreground mb-2">
                As políticas de cancelamento variam de acordo com o imóvel:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Flexível:</strong> Reembolso total até 7 dias antes do check-in</li>
                <li><strong>Moderada:</strong> Reembolso de 50% até 14 dias antes do check-in</li>
                <li><strong>Rígida:</strong> Reembolso de 50% até 30 dias antes do check-in</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">7. Responsabilidades dos Hóspedes</h2>
              <p className="text-muted-foreground mb-2">
                Os hóspedes devem:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Respeitar as regras do condomínio Porto 2 Life</li>
                <li>Manter o número de ocupantes conforme reservado</li>
                <li>Não realizar festas ou eventos sem autorização prévia</li>
                <li>Respeitar o horário de silêncio (22h às 8h)</li>
                <li>Deixar o imóvel nas mesmas condições em que encontrou</li>
                <li>Reportar imediatamente qualquer dano ou problema</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">8. Responsabilidade</h2>
              <p className="text-muted-foreground">
                A Live Stay atua como intermediária entre proprietários e hóspedes. Não nos 
                responsabilizamos por danos ao imóvel, cancelamentos pelos proprietários, condições 
                diferentes das anunciadas ou eventos de força maior que impeçam a estadia.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">9. Propriedade Intelectual</h2>
              <p className="text-muted-foreground">
                Todo o conteúdo da plataforma, incluindo textos, gráficos, logos e software, é
                propriedade da Live Stay ou de seus licenciadores e está protegido por leis de
                direitos autorais.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">10. Modificações dos Termos</h2>
              <p className="text-muted-foreground">
                Reservamo-nos o direito de modificar estes termos a qualquer momento. As alterações
                entrarão em vigor imediatamente após a publicação. O uso continuado da plataforma
                após as alterações constitui aceitação dos novos termos.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">11. Lei Aplicável</h2>
              <p className="text-muted-foreground">
                Estes termos são regidos pelas leis brasileiras. Qualquer disputa será resolvida nos 
                tribunais competentes de Recife, PE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-3">12. Contato</h2>
              <p className="text-muted-foreground">
                Para questões sobre estes Termos de Uso, entre em contato através da página de{" "}
                <Link to="/fale-conosco" className="text-primary hover:underline font-medium">
                  Fale Conosco
                </Link>
                {" "}ou pelo email:{" "}
                <a 
                  href="mailto:livestaybrasil@gmail.com" 
                  className="text-primary hover:underline font-medium"
                >
                  livestaybrasil@gmail.com
                </a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;
